<script setup lang="ts">
import { Popover, PopoverButton, PopoverPanel, TransitionRoot } from '@headlessui/vue';
import Button from '../General/Button.vue';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../configs/text';
import handIcon from '../../assets/icons/hand.svg';
import chevronUpWhite from '../../assets/icons/chevron-up-white.svg';
import chevronRightBlue from '../../assets/icons/chevron-right-blue.svg';
import handCrossIcon from '../../assets/icons/hand-white-cross.svg';
import { useClassStore, useSignalRStore } from '../../stores';
import { computed, ref } from 'vue';
import { ClassStudentInfo } from '../../types/class';
import Modal from '../General/Modal.vue';
import SetPointStudent from './SetPointStudent.vue';

const classStore = useClassStore()
const signalr = useSignalRStore()

const modalSetPointStudentShow = ref<boolean>(false)
const selectedStudent = ref<ClassStudentInfo | null>(null)

const raiseHandStudents = computed<ClassStudentInfo[]>(() => {
    return classStore.raiseHandStudentIds.reduce<ClassStudentInfo[]>((students, studentId) => {
        const student = classStore.selectedClassDetails?.students?.find(student => student.id === studentId)
        if (student) {
            students.push(student)
        }
        return students
    }, [])
})

function toggleRaiseHand() {
    if (classStore.selectedClassDetails?.allowRaiseHand) {
        signalr.disableRaiseHand()
        signalr.removeRequestRaiseHand()
        return;
    }

    signalr.allowRaiseHand()
}

function handleRaiseHandStudentClick(student: ClassStudentInfo) {
    selectedStudent.value = student
    modalSetPointStudentShow.value = true
}

const { t } = useI18n()
</script>

<template>
    <Popover class="relative" v-slot="{ open }">
        <div class="flex items-center gap-3">
            <PopoverButton class="shrink-0">
                <div class="relative">
                    <Button :color="classStore.selectedClassDetails?.allowRaiseHand ? 'secondary' : 'gray'">
                        <img :src="chevronUpWhite" class="size-5" />
                        <span>{{ t(TEXT.raise_hand) }}</span>
                    </Button>
                    <div v-if="raiseHandStudents.length"
                        class="flex items-center justify-center rounded-full bg-red-500 text-white absolute -right-2 -top-2 font-bold text-xs p-1 min-w-8">
                        {{ raiseHandStudents.length }}
                    </div>
                </div>
            </PopoverButton>
            <Button :color="classStore.selectedClassDetails?.allowRaiseHand ? 'info' : 'gray'" @click="toggleRaiseHand"
                class="!p-0 size-[44px] shrink-0">
                <img :src="classStore.selectedClassDetails?.allowRaiseHand ? handIcon : handCrossIcon"
                    class="size-6 brightness-0 invert" />
            </Button>
        </div>
        <TransitionRoot appear :show="open" as="template" enter="duration-300 ease-out" enter-from="opacity-0"
            enter-to="opacity-100" leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
            <PopoverPanel class="absolute bottom-[calc(100%+0.5rem)] z-10 bg-white">
                <Button v-if="raiseHandStudents.length" color="light" class="!px-3">
                    <div class="flex flex-col items-start gap-2 w-max max-h-[40vh] p-1 overflow-auto">
                        <Button color="white" class="w-full !justify-between !gap-3 hover:!bg-sky-300 hover:!border-sky-300 hover:!shadow-[4px_4px_0_0_#0EA5E9] rounded-xl group" v-for="student in raiseHandStudents" @click="handleRaiseHandStudentClick(student)">
                            <span class="text-blue-700 group-hover:!text-white group-focus:!text-white">{{ student.full_name }}</span>
                            <img :src="chevronRightBlue" class="size-5" />
                        </Button>
                    </div>
                </Button>
            </PopoverPanel>
        </TransitionRoot>
    </Popover>

    <Modal v-model="modalSetPointStudentShow" size="medium">
        <SetPointStudent v-if="selectedStudent !== null" :student="selectedStudent"
            @update="modalSetPointStudentShow = false" />
    </Modal>
</template>